import { FC, useEffect, useMemo, useState } from 'react'
import { Box, SelectChangeEvent, Typography } from '@mui/material'

/* Utils */
import {
  Benchmark,
  BenchmarkScore,
  BenchmarkScoreCategory,
  CategoryActivityItem,
  CategoryFramework
} from '../../../../../models'
import { benchmarkingSelectOptions, sortCategoriesByActivityOrder } from './benchmarking.constants'

/* Components */
import BenchmarkingSelectField from './components/BenchmarkingSelectField'
import BenchmarkCard from './components/BenchmarkCard'
import { useThemeColors } from 'hooks/useThemeColors'
import { BenchmarkingContainer, EmptyBenchmarkingContainer } from './benchmarking.styles'
import { InfoChipsWrapper } from '../../dashboard.styles'

const emptyData: BenchmarkScoreCategory[] = [
  {
    category_id: '',
    category_name: 'Threat Detection',
    framework_id: CategoryFramework.Onyxia,
    framework_name: 'onyxia',
    function_id: '',
    function_name: '',
    summary: {
      less: null,
      equal: null,
      more: null
    },
    data: [
      {
        cpi_display_name: 'Placeholder',
        cpi_name: 'Placeholder',
        cpi_title: 'Placeholder'
      }
    ]
  },
  {
    category_id: '',
    category_name: 'Vulnerability Management',
    summary: {
      less: null,
      equal: null,
      more: null
    },
    framework_id: CategoryFramework.Onyxia,
    framework_name: 'onyxia',
    function_id: '',
    function_name: '',
    data: [
      {
        cpi_display_name: 'Placeholder',
        cpi_name: 'Placeholder',
        cpi_title: 'Placeholder'
      }
    ]
  },
  {
    category_id: '',
    category_name: 'Training & Awareness',
    summary: {
      less: null,
      equal: null,
      more: null
    },
    framework_id: CategoryFramework.Onyxia,
    framework_name: 'onyxia',
    function_id: '',
    function_name: '',
    data: [
      {
        cpi_display_name: 'Placeholder',
        cpi_name: 'Placeholder',
        cpi_title: 'Placeholder'
      }
    ]
  },
  {
    category_id: '',
    category_name: 'Cloud Security',
    summary: {
      less: null,
      equal: null,
      more: null
    },
    framework_id: CategoryFramework.Onyxia,
    framework_name: 'onyxia',
    function_id: '',
    function_name: '',
    data: [
      {
        cpi_display_name: 'Placeholder',
        cpi_name: 'Placeholder',
        cpi_title: 'Placeholder'
      }
    ]
  }
]

interface Props {
  benchmark: Benchmark | null
  cpiTitles: {
    displayName: string
    name: string
    title: string
    category: string
  }[]
  isEmpty: boolean
  categoryActivity: CategoryActivityItem[]
}

const Benchmarking: FC<Props> = ({ benchmark, cpiTitles, isEmpty, categoryActivity }) => {
  const getColor = useThemeColors()
  const [selectedBenchmark, setSelectedBenchmark] = useState<string>(benchmarkingSelectOptions[0])
  const [score, setScore] = useState<BenchmarkScore[]>([])
  const [loaded, setLoaded] = useState(false)
  const [currentBenchmark, setCurrentBenchmark] = useState<BenchmarkScore | null>(null)

  useEffect(() => {
    if (benchmark && benchmark.score.length > 0) {
      setScore(benchmark.score)
      const findItem = benchmark.score.find((item) => item.industry_name === 'All Industries')
      const item = findItem || benchmark.score[0]
      setCurrentBenchmark(item)
      setLoaded(true)
    }
  }, [benchmark])

  const handleCompareToChange = (e: SelectChangeEvent<typeof selectedBenchmark>) => {
    const { value } = e.target
    setSelectedBenchmark(value)
    const newBenchmark = score.find((n) => n.industry_name === value)
    if (newBenchmark) {
      setCurrentBenchmark(newBenchmark)
    }
  }

  const checkHasBenchmark = (category: BenchmarkScoreCategory) => {
    if (!category.summary) return false

    return !!category.summary
  }

  const sortedCategories = useMemo(
    () => sortCategoriesByActivityOrder(currentBenchmark?.categories ?? [], categoryActivity),
    [currentBenchmark, categoryActivity]
  )

  if (!loaded || isEmpty) {
    return (
      <EmptyBenchmarkingContainer>
        <Box className="wrapper">
          <Box className="header">
            <Box className="header-title">
              <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.14844 0.0732422C7.30093 0.0732422 7.44685 0.134358 7.55469 0.242188C7.66252 0.350021 7.72363 0.495938 7.72363 0.648438V1.92969L11.5244 1.08496C11.6575 1.05555 11.7965 1.075 11.917 1.13867C12.0375 1.20234 12.1319 1.30614 12.1826 1.43262L14.1826 6.43262C14.2101 6.5011 14.2241 6.57463 14.2236 6.64844L14.2188 6.78809C14.1727 7.4726 13.7889 7.94999 13.3008 8.25781C12.7826 8.58451 12.1432 8.72363 11.6484 8.72363C11.1537 8.72363 10.5143 8.58451 9.99609 8.25781C9.47541 7.92947 9.07324 7.40816 9.07324 6.64844C9.07281 6.57463 9.08673 6.5011 9.11426 6.43262L10.7139 2.44531L7.72363 3.10938V11.0732H8.64844C8.80093 11.0732 8.94685 11.1344 9.05469 11.2422C9.16252 11.35 9.22363 11.4959 9.22363 11.6484C9.22363 11.8009 9.16252 11.9469 9.05469 12.0547C8.94685 12.1625 8.80093 12.2236 8.64844 12.2236H5.64844C5.49594 12.2236 5.35002 12.1625 5.24219 12.0547C5.13436 11.9469 5.07324 11.8009 5.07324 11.6484C5.07324 11.4959 5.13436 11.35 5.24219 11.2422C5.35002 11.1344 5.49594 11.0732 5.64844 11.0732H6.57324V3.36621L3.43262 4.0625L5.18262 8.43262C5.21015 8.5011 5.22406 8.57463 5.22363 8.64844L5.21875 8.78809C5.17271 9.4726 4.78894 9.94999 4.30078 10.2578C3.78259 10.5845 3.14316 10.7236 2.64844 10.7236C2.15372 10.7236 1.51428 10.5845 0.996094 10.2578C0.507944 9.94999 0.124167 9.4726 0.078125 8.78809L0.0732422 8.64844C0.072811 8.57463 0.0867255 8.5011 0.114258 8.43262L2.10938 3.44824V3.44629L2.11426 3.43262C2.14927 3.34616 2.20513 3.2692 2.27637 3.20898C2.34756 3.14887 2.43245 3.10702 2.52344 3.08691L6.57324 2.18652V0.648438C6.57324 0.495941 6.63436 0.35002 6.74219 0.242188C6.85002 0.134357 6.99594 0.0732451 7.14844 0.0732422ZM1.23047 8.74121C1.26905 9.03939 1.49301 9.2466 1.7832 9.38184C2.07768 9.51905 2.4223 9.57324 2.64844 9.57324C2.87457 9.57324 3.21919 9.51905 3.51367 9.38184C3.80382 9.24661 4.0268 9.03932 4.06543 8.74121L2.64844 5.19531L1.23047 8.74121ZM10.2305 6.74121C10.2691 7.03939 10.493 7.2466 10.7832 7.38184C11.0777 7.51905 11.4223 7.57324 11.6484 7.57324C11.8746 7.57324 12.2192 7.51905 12.5137 7.38184C12.8038 7.24661 13.0268 7.03932 13.0654 6.74121L11.6484 3.19531L10.2305 6.74121Z"
                fill={getColor('text-primary')}
                stroke={getColor('text-primary')}
                strokeWidth="0.15"
              />
            </svg>
              <Typography className="title">Benchmarking</Typography>
            </Box>
            <Box className="select-wrapper">
              <BenchmarkingSelectField
                options={['All Industries']}
                value="All Industries"
                handleChange={handleCompareToChange}
              />
            </Box>
          </Box>
          <Box className="card-container">
            {emptyData.map((n, i) => (
              <BenchmarkCard
                category={n}
                key={n.category_id || i}
                i={i}
                cpiTitles={emptyData.map((n) => ({
                  displayName: n.category_name,
                  name: 'Placeholder',
                  title: 'Placeholder',
                  category: 'Placeholder'
                }))}
                hasBenchmark={false}
              />
            ))}
          </Box>
        </Box>
      </EmptyBenchmarkingContainer>
    )
  }
  return (
    <BenchmarkingContainer isempty={isEmpty ? 'true' : 'false'}>
      <Box className="wrapper" id="benchmarking">
        <Box className="header">
          <Box className="header-title">
            <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.14844 0.0732422C7.30093 0.0732422 7.44685 0.134358 7.55469 0.242188C7.66252 0.350021 7.72363 0.495938 7.72363 0.648438V1.92969L11.5244 1.08496C11.6575 1.05555 11.7965 1.075 11.917 1.13867C12.0375 1.20234 12.1319 1.30614 12.1826 1.43262L14.1826 6.43262C14.2101 6.5011 14.2241 6.57463 14.2236 6.64844L14.2188 6.78809C14.1727 7.4726 13.7889 7.94999 13.3008 8.25781C12.7826 8.58451 12.1432 8.72363 11.6484 8.72363C11.1537 8.72363 10.5143 8.58451 9.99609 8.25781C9.47541 7.92947 9.07324 7.40816 9.07324 6.64844C9.07281 6.57463 9.08673 6.5011 9.11426 6.43262L10.7139 2.44531L7.72363 3.10938V11.0732H8.64844C8.80093 11.0732 8.94685 11.1344 9.05469 11.2422C9.16252 11.35 9.22363 11.4959 9.22363 11.6484C9.22363 11.8009 9.16252 11.9469 9.05469 12.0547C8.94685 12.1625 8.80093 12.2236 8.64844 12.2236H5.64844C5.49594 12.2236 5.35002 12.1625 5.24219 12.0547C5.13436 11.9469 5.07324 11.8009 5.07324 11.6484C5.07324 11.4959 5.13436 11.35 5.24219 11.2422C5.35002 11.1344 5.49594 11.0732 5.64844 11.0732H6.57324V3.36621L3.43262 4.0625L5.18262 8.43262C5.21015 8.5011 5.22406 8.57463 5.22363 8.64844L5.21875 8.78809C5.17271 9.4726 4.78894 9.94999 4.30078 10.2578C3.78259 10.5845 3.14316 10.7236 2.64844 10.7236C2.15372 10.7236 1.51428 10.5845 0.996094 10.2578C0.507944 9.94999 0.124167 9.4726 0.078125 8.78809L0.0732422 8.64844C0.072811 8.57463 0.0867255 8.5011 0.114258 8.43262L2.10938 3.44824V3.44629L2.11426 3.43262C2.14927 3.34616 2.20513 3.2692 2.27637 3.20898C2.34756 3.14887 2.43245 3.10702 2.52344 3.08691L6.57324 2.18652V0.648438C6.57324 0.495941 6.63436 0.35002 6.74219 0.242188C6.85002 0.134357 6.99594 0.0732451 7.14844 0.0732422ZM1.23047 8.74121C1.26905 9.03939 1.49301 9.2466 1.7832 9.38184C2.07768 9.51905 2.4223 9.57324 2.64844 9.57324C2.87457 9.57324 3.21919 9.51905 3.51367 9.38184C3.80382 9.24661 4.0268 9.03932 4.06543 8.74121L2.64844 5.19531L1.23047 8.74121ZM10.2305 6.74121C10.2691 7.03939 10.493 7.2466 10.7832 7.38184C11.0777 7.51905 11.4223 7.57324 11.6484 7.57324C11.8746 7.57324 12.2192 7.51905 12.5137 7.38184C12.8038 7.24661 13.0268 7.03932 13.0654 6.74121L11.6484 3.19531L10.2305 6.74121Z"
                fill={getColor('text-primary')}
                stroke={getColor('text-primary')}
                strokeWidth="0.15"
              />
            </svg>
            <Typography className="title">Benchmarking</Typography>
          </Box>

          <Box className="select-wrapper">
            <BenchmarkingSelectField
              options={score.map((scoreItem) => scoreItem.industry_name).sort()}
              value={selectedBenchmark}
              handleChange={handleCompareToChange}
            />
          </Box>
        </Box>

        <InfoChipsWrapper forbenchmark="true">
          <Box className="chip">
            <Box className="chip-circle less" /> <Typography>Less Strict SLA</Typography>
          </Box>
          <Box className="chip">
            <Box className="chip-circle same" /> <Typography>Same SLA</Typography>
          </Box>
          <Box className="chip">
            <Box className="chip-circle more" /> <Typography>More Strict SLA</Typography>
          </Box>
        </InfoChipsWrapper>

        <Box className="card-container">
          {sortedCategories.map((category, i) => (
            <BenchmarkCard
              category={category}
              key={category.category_id || i}
              i={i}
              cpiTitles={cpiTitles}
              hasBenchmark={checkHasBenchmark(category)}
            />
          ))}
        </Box>
      </Box>
    </BenchmarkingContainer>
  )
}

export default Benchmarking
